import axios from 'axios';
import { SCOPED_CACHE_PREFIXES } from '@/utils/auth/session_constants.js';

const SESSION_EXPIRY_KEY = 'auth_expires_at';
const SESSION_WARNING_OFFSET_MS = 2 * 60 * 1000;
const SESSION_REFRESH_URL = process.env.VUE_APP_SESSION_REFRESH_URL || '';
const DEFAULT_SESSION_TIMEOUT_MINUTES = Number(process.env.VUE_APP_SESSION_TIMEOUT_MINUTES || 0);

const AUTH_STORAGE_KEYS = [
  'token',
  'otp_verified',
  'is_aic_one_login',
  'auth_redirect_reason',
  'auth_expires_at',
  'current_user_id',
  'current_lead_id',
  'current_role',
  'current_lead_name',
  'user_id',
  'user_role',
  'display_name',
  'project_id',
  'company_name',
  'entity_type_id',
  'entity_profile_id',
  'lead_id',
  'lead_name',
  'role_type',
  'current_platform',
  'current_organization',
  'is_organization_admin',
  'is_platform_admin',
];

const AUTH_EXPIRY_MESSAGE_PARTS = [
  'authentication credentials not provided',
  'credentials',
  'session',
  'expired',
  'unauthorized',
  'forbidden',
];

let router_instance = null;
let store_instance = null;
let interceptor_installed = false;
let modal_promise = null;
let warning_timer = null;
let expiry_timer = null;
let refresh_promise = null;

function parse_response_payload(value) {
  if (typeof value !== 'string') {
    return value || {};
  }

  try {
    return JSON.parse(value);
  } catch (parse_error) {
    void parse_error;
    return { message: value };
  }
}

function response_message(error) {
  const payload = parse_response_payload(error?.response?.data);
  return String(
    payload?.message
    || payload?.detail
    || payload?.error
    || error?.message
    || ''
  );
}

function has_auth_context(config = {}) {
  const headers = config.headers || {};
  return Boolean(
    sessionStorage.getItem('token')
    || headers.Authorization
    || headers.authorization
  );
}

function is_auth_expiry_error(error) {
  const status = Number(error?.response?.status || 0);

  if (status === 401) {
    return true;
  }

  if (status !== 403) {
    return false;
  }

  const message = response_message(error).toLowerCase();
  return AUTH_EXPIRY_MESSAGE_PARTS.some((part) => message.includes(part));
}

function friendly_session_error(error) {
  if (error?.response) {
    error.response.data = {
      message: 'Your session has expired. Please choose an option to continue.',
    };
  }
  error.__session_expired = true;
  return error;
}

function clear_storage_area(storage) {
  AUTH_STORAGE_KEYS.forEach((key) => storage.removeItem(key));

  // Configuration caches are dynamically keyed and therefore cannot be
  // listed in AUTH_STORAGE_KEYS. They must not survive a user change.
  Array.from({ length: storage.length }, (_, index) => storage.key(index))
    .filter((key) => key && SCOPED_CACHE_PREFIXES.some((prefix) => key.startsWith(prefix)))
    .forEach((key) => {
      storage.removeItem(key);
    });
}

export function clear_auth_storage() {
  if (typeof window === 'undefined') {
    return;
  }

  clear_storage_area(window.sessionStorage);
  clear_storage_area(window.localStorage);
}

function redirect_to_login(message = 'Your session has expired. Please sign in again.') {
  clear_auth_timers();
  clear_auth_storage();
  sessionStorage.setItem('auth_redirect_reason', message);

  if (store_instance) {
    store_instance.dispatch('resetStore');
  }

  if (router_instance?.currentRoute?.value?.name !== 'UserLogin') {
    router_instance.push({ name: 'UserLogin' });
  }
}

function ensure_modal_element() {
  if (!window.bootstrap?.Modal) {
    redirect_to_login('Your session has expired. Please sign in again.');
    return null;
  }

  const existing = document.getElementById('global_session_expiry_modal');
  if (existing) {
    return existing;
  }

  const wrapper = document.createElement('div');
  wrapper.innerHTML = `
    <div class="modal fade" id="global_session_expiry_modal" tabindex="-1" aria-labelledby="global_session_expiry_modal_label" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content bg-white rounded-3 p-0 shadow-lg">
          <div class="modal-header border-bottom-0 cute-header shadow-lg">
            <p class="modal-title text-white text-xs mb-0" id="global_session_expiry_modal_label">Session Expired</p>
          </div>
          <div class="modal-body">
            <p class="text-dark text-xs mb-0">
              Your session has expired. Would you like to stay logged in or log out?
            </p>
            <p class="text-danger text-xs fw-bold mt-3 mb-0 d-none" data-session-error></p>
          </div>
          <div class="modal-footer border-0 p-2 d-flex flex-column flex-sm-row justify-content-end gap-2">
            <button type="button" class="btn btn-light border btn-sm rounded-small text-xs" data-session-logout>
              Log Out
            </button>
            <button type="button" class="btn btn-primary-dark btn-sm rounded-small text-xs shadow text-dark" data-session-stay>
              Stay Logged In
            </button>
          </div>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(wrapper.firstElementChild);
  return document.getElementById('global_session_expiry_modal');
}

function set_modal_title(modal_element, title) {
  if (!modal_element) {
    return;
  }

  const title_element = modal_element.querySelector('#global_session_expiry_modal_label');
  if (title_element) {
    title_element.textContent = title;
  }
}

function show_modal_error(modal_element, message) {
  if (!modal_element) {
    return;
  }

  const error_element = modal_element.querySelector('[data-session-error]');
  if (!error_element) {
    return;
  }

  error_element.textContent = message;
  error_element.classList.remove('d-none');
}

function open_session_modal({ title = 'Session Expired' } = {}) {
  if (modal_promise) {
    return modal_promise;
  }

  modal_promise = new Promise((resolve) => {
    const modal_element = ensure_modal_element();
    if (!modal_element) {
      modal_promise = null;
      resolve('logout');
      return;
    }

    set_modal_title(modal_element, title);
    const modal = window.bootstrap.Modal.getInstance(modal_element)
      || new window.bootstrap.Modal(modal_element, {
        backdrop: 'static',
        keyboard: false,
      });

    const stay_button = modal_element.querySelector('[data-session-stay]');
    const logout_button = modal_element.querySelector('[data-session-logout]');

    const cleanup = () => {
      stay_button.removeEventListener('click', handle_stay);
      logout_button.removeEventListener('click', handle_logout);
      modal_promise = null;
    };
    const handle_stay = () => {
      cleanup();
      resolve('stay');
    };
    const handle_logout = () => {
      cleanup();
      resolve('logout');
    };

    stay_button.addEventListener('click', handle_stay);
    logout_button.addEventListener('click', handle_logout);
    modal.show();
  });

  return modal_promise;
}

function close_session_modal() {
  const modal_element = document.getElementById('global_session_expiry_modal');
  if (!modal_element) {
    return;
  }

  if (!window.bootstrap?.Modal) {
    return;
  }

  const modal = window.bootstrap.Modal.getInstance(modal_element);
  if (modal) {
    modal.hide();
  }
}

async function refresh_session() {
  if (!SESSION_REFRESH_URL) {
    throw new Error('No session refresh endpoint is configured.');
  }

  if (!refresh_promise) {
    refresh_promise = axios.post(
      SESSION_REFRESH_URL,
      {},
      { skipSessionExpiryHandler: true, withCredentials: true }
    ).finally(() => {
      refresh_promise = null;
    });
  }

  const response = await refresh_promise;
  const payload = parse_response_payload(response?.data);

  if (payload?.token) {
    sessionStorage.setItem('token', payload.token);
  }

  set_session_expiry_from_response(payload);
  return payload;
}

function extend_local_session() {
  if (DEFAULT_SESSION_TIMEOUT_MINUTES <= 0) {
    throw new Error('No session timeout is configured.');
  }

  const token = sessionStorage.getItem('token');
  if (!token) {
    throw new Error('No active session token was found.');
  }

  set_session_expiry_from_response({
    expires_in: DEFAULT_SESSION_TIMEOUT_MINUTES * 60,
  });
}

function retry_original_request(retry_request, original_error) {
  if (!retry_request || !original_error?.config) {
    return null;
  }

  const retry_config = {
    ...original_error.config,
    _sessionRetry: true,
  };
  return axios(retry_config);
}

function reject_session_error(error, original_error) {
  if (original_error) {
    throw friendly_session_error(original_error);
  }
  throw error;
}

async function renew_local_session_or_redirect({ retry_request, original_error } = {}) {
  close_session_modal();

  try {
    extend_local_session();
    return await retry_original_request(retry_request, original_error);
  } catch (session_error) {
    show_modal_error(
      document.getElementById('global_session_expiry_modal'),
      'Please sign in again to continue.'
    );
    redirect_to_login('Your session has expired. Please sign in again.');
    return reject_session_error(session_error, original_error);
  }
}

function renew_remote_session_or_redirect({ retry_request, original_error } = {}) {
  return refresh_session()
    .then(() => {
      close_session_modal();
      return retry_original_request(retry_request, original_error);
    })
    .catch((refresh_error) => {
      show_modal_error(
        document.getElementById('global_session_expiry_modal'),
        'We could not renew your session. Please sign in again.'
      );
      close_session_modal();
      redirect_to_login('We could not renew your session. Please sign in again.');
      throw friendly_session_error(refresh_error);
    });
}

async function renew_or_redirect(options = {}) {
  if (!SESSION_REFRESH_URL) {
    return renew_local_session_or_redirect(options);
  }

  return renew_remote_session_or_redirect(options);
}

async function handle_session_choice(original_error) {
  const choice = await open_session_modal();

  if (choice === 'logout') {
    close_session_modal();
    redirect_to_login('You have been logged out.');
    throw friendly_session_error(original_error);
  }

  return renew_or_redirect({ retry_request: true, original_error });
}

async function handle_scheduled_session_prompt(title) {
  const choice = await open_session_modal({ title });

  if (choice === 'logout') {
    close_session_modal();
    redirect_to_login('You have been logged out.');
    return;
  }

  await renew_or_redirect().catch(() => null);
}

function clear_auth_timers() {
  if (warning_timer) {
    clearTimeout(warning_timer);
    warning_timer = null;
  }
  if (expiry_timer) {
    clearTimeout(expiry_timer);
    expiry_timer = null;
  }
}

export function set_session_expiry_from_response(response_data = {}) {
  const expiry_time = response_session_expiry_time(response_data);

  if (!Number.isFinite(expiry_time) || expiry_time <= Date.now()) {
    return;
  }

  sessionStorage.setItem(SESSION_EXPIRY_KEY, String(expiry_time));
  schedule_session_timers();
}

function response_session_expiry_time(response_data = {}) {
  const expires_at = response_data.expires_at
    || response_data.token_expires_at
    || response_data.session_expires_at;
  const expires_in = Number(response_data.expires_in || response_data.expires_in_seconds || 0);

  if (expires_at) {
    return new Date(expires_at).getTime();
  }

  if (expires_in > 0) {
    return Date.now() + expires_in * 1000;
  }

  if (DEFAULT_SESSION_TIMEOUT_MINUTES > 0) {
    return Date.now() + DEFAULT_SESSION_TIMEOUT_MINUTES * 60 * 1000;
  }

  return 0;
}

export function schedule_session_timers() {
  clear_auth_timers();
  const expiry_time = Number(sessionStorage.getItem(SESSION_EXPIRY_KEY) || 0);
  if (!expiry_time || expiry_time <= Date.now()) {
    return;
  }

  const warning_delay = Math.max(expiry_time - Date.now() - SESSION_WARNING_OFFSET_MS, 0);
  const expiry_delay = Math.max(expiry_time - Date.now(), 0);

  warning_timer = setTimeout(() => {
    handle_scheduled_session_prompt('Session Expiring Soon');
  }, warning_delay);

  expiry_timer = setTimeout(() => {
    handle_scheduled_session_prompt('Session Expired');
  }, expiry_delay);
}

export function install_session_expiry_handler({ router, store } = {}) {
  router_instance = router;
  store_instance = store;
  schedule_session_timers();

  if (interceptor_installed) {
    return;
  }

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (
        error?.config?.skipSessionExpiryHandler
        || error?.config?._sessionRetry
        || !has_auth_context(error?.config)
        || !is_auth_expiry_error(error)
      ) {
        return Promise.reject(error);
      }

      return handle_session_choice(error);
    }
  );
  interceptor_installed = true;
}
